<script setup>
/**
 * SentGift: the sender's page for a reading he sent (2026-10-19, Chris: "the
 * one letter"). It replaces CarePackageLanding's sender view.
 *
 * Order: the switch ("Your reading" first and always the default; the other
 * shows what she receives), the letter (picture, his note over its edge, the
 * title once, each card once with its phrase and passage, "All four together"
 * on the gold thread), then a slim status band (where it is now and the one
 * next step, in relative time), then the actions (copy the link again, take
 * it back). On a wide screen the switch, the band and the actions stand in a
 * sticky side column beside the letter; one version shows at a time, never
 * side by side. No editing of any of it: the note stays as he sent it.
 *
 * The history (gift.history) comes from the same backend helper as the
 * Journal's Sent tab, so the two never disagree; the full timeline lives
 * there, not here.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { careLink, carePrepareLink, careRevoke } from '@/api-service'
import { handOver, prefersShareSheet } from '@/utils/care-link'
import { letterVersions, statusLine, VERSION_LABELS } from '@/utils/gift-letter'
import { store } from '@/store'
import GiftLetter from '@/components/ui/GiftLetter.vue'

const props = defineProps({
  data: { type: Object, required: true },   // reading-preview payload, viewer_role 'sender'
})
const emit = defineEmits(['leave'])

const gift = computed(() => props.data.gift || {})
const forName = computed(() => gift.value.for_name || 'them')
const versions = computed(() => letterVersions(props.data))
const twoVersions = computed(() => !!versions.value.mine)
// "Your reading" is first and always the default (Chris, 2026-10-19). The
// send screen's "See what <Name> gets" opens it on her version once
// (`side=hers`), and the address forgets it so a reload is the default again.
const showing = ref(handedHers() ? 'hers' : 'mine')
function handedHers() {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('side') !== 'hers') return false
    url.searchParams.delete('side')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
    return true
  } catch (e) { return false }
}
const shown = computed(() => (twoVersions.value && showing.value === 'mine') ? versions.value.mine : versions.value.hers)
const tone = computed(() => (twoVersions.value && showing.value === 'mine') ? 'mine' : 'hers')
const labels = computed(() => ({ mine: VERSION_LABELS.mine(), hers: VERSION_LABELS.hers(gift.value.for_name) }))

// The band: "now" ticks once a minute so "a moment ago" grows up.
const now = ref(new Date())
let tick = null
onMounted(() => { tick = setInterval(() => { now.value = new Date() }, 60000) })
onBeforeUnmount(() => clearInterval(tick))

const takenBack = ref(false)
const revoked = computed(() => !!gift.value.revoked || takenBack.value)
const opened = computed(() => !!gift.value.opened)
const status = computed(() => statusLine(gift.value.history, { now: now.value, revokedNow: takenBack.value }))
const canAct = computed(() => !opened.value && !revoked.value)

// The link to paste again (the same one until someone takes it; the link
// alone, as the send screen copies it). Fetched when the page opens, so the
// tap shares or copies inside its own gesture (iPhone Safari drops a share
// started after a request); without it, the tap makes it first.
const err = ref('')
const busy = ref(false)
const linkState = ref('')        // '', 'making', 'copied', 'shared', 'failed'
const preparedLink = ref('')
const readingId = () => props.data.short_uuid || new URLSearchParams(window.location.search).get('reading') || ''
function failed(e) {
  const said = e && e.response && e.response.data && e.response.data.error
  err.value = said ? `${said}.` : 'That did not go through. Try again.'
}
async function prepare() {
  try { const r = await carePrepareLink(store.session_id, readingId()); preparedLink.value = (r && r.link) || '' }
  catch (e) { preparedLink.value = '' }
}
onMounted(() => { if (canAct.value) prepare() })
function copyAgain() {
  if (linkState.value === 'making') return
  err.value = ''
  if (preparedLink.value) return finish(handOver({ link: preparedLink.value }, { share: prefersShareSheet() }))
  return copySlow()
}
async function finish(pending) {
  const outcome = await pending
  if (outcome === 'cancelled') return
  linkState.value = outcome
  try { await careLink(store.session_id, readingId()) } catch (e) { failed(e) }
}
async function copySlow() {
  linkState.value = 'making'
  try {
    const r = await careLink(store.session_id, readingId())
    preparedLink.value = r.link
    linkState.value = await handOver({ link: r.link }, { share: prefersShareSheet() })
  } catch (e) { linkState.value = ''; failed(e) }
}
const copyLabel = computed(() => ({
  making: 'One moment…', copied: 'Link copied', shared: 'Sent',
}[linkState.value] || 'Copy the link again'))

const askTakeBack = ref(false)
async function takeBack() {
  if (busy.value) return
  busy.value = true; err.value = ''
  try { await careRevoke(store.session_id, readingId()); takenBack.value = true; askTakeBack.value = false }
  catch (e) { failed(e) } finally { busy.value = false }
}
const roomName = computed(() => props.data.reader_name ? `${props.data.reader_name}'s room` : 'the room')
</script>

<template>
  <section class="sg" aria-label="The reading you sent" data-testid="care-sender-view">
    <aside class="sg-side">
      <div class="sg-top" :class="{ 'one-version': !twoVersions }">
        <p v-if="twoVersions" class="sg-to">You sent this to {{ forName }}</p>
        <div v-if="twoVersions" class="sg-switch" role="group" aria-label="Which version" data-testid="version-switch">
          <button type="button" class="sg-opt" :class="{ on: showing === 'mine' }" :aria-pressed="showing === 'mine'"
                  data-testid="version-mine" @click="showing = 'mine'">{{ labels.mine }}</button>
          <button type="button" class="sg-opt" :class="{ on: showing === 'hers' }" :aria-pressed="showing === 'hers'"
                  data-testid="version-hers" @click="showing = 'hers'">{{ labels.hers }}</button>
        </div>
        <p v-else class="sg-one">You sent this to {{ forName }}</p>
        <button type="button" class="sg-x" aria-label="Close" data-testid="care-close"
                @click="emit('leave', data.reader_subdomain)"><i class="fas fa-times" aria-hidden="true"></i></button>
      </div>

      <div class="sg-foot">
        <div class="sg-band" role="status" data-testid="sent-status">
          <p class="sg-lead" data-testid="care-close-line"><span v-if="status.quote !== undefined" class="sg-heart" aria-hidden="true">♥ </span><template v-if="status.word">{{ status.lead }} <b>{{ status.word }}</b>{{ status.tail }}</template><template v-else>{{ status.lead }}</template><template v-if="status.quote">{{ ' ' }}<span class="sg-quote">“{{ status.quote }}”</span></template></p>
          <p v-if="status.next" class="sg-next" data-testid="sent-next">{{ status.next }}</p>
        </div>

        <div class="sg-actions" data-testid="sent-actions">
          <template v-if="canAct">
            <button type="button" class="sg-copy" data-testid="care-copy-again" :disabled="linkState === 'making'" @click="copyAgain">
              <i :class="(linkState === 'copied' || linkState === 'shared') ? 'fas fa-check' : 'fas fa-link'" aria-hidden="true"></i>
              <span>{{ copyLabel }}</span>
            </button>
            <p v-if="linkState === 'copied'" class="sg-hint">Paste it to {{ forName }}. It opens for them only.</p>
            <button v-if="!askTakeBack" type="button" class="sg-link" data-testid="care-take-back" @click="askTakeBack = true">Take it back</button>
            <div v-else class="sg-confirm">
              <p>{{ forName }} will not be able to open it.</p>
              <div class="sg-confirm-row">
                <button type="button" class="sg-link sg-strong" data-testid="care-take-back-yes" :disabled="busy" @click="takeBack">Yes, take it back</button>
                <button type="button" class="sg-link" @click="askTakeBack = false">Keep it</button>
              </div>
            </div>
          </template>
          <p v-if="err" class="sg-err" role="alert">{{ err }}</p>
          <button type="button" class="sg-leave" data-testid="care-leave" @click="emit('leave', data.reader_subdomain)">Back to {{ roomName }}</button>
        </div>
      </div>
    </aside>

    <div class="sg-letter">
      <Transition name="sg-fade" mode="out-in">
        <GiftLetter :key="tone" :tone="tone" :image="data.image_url || ''" :note="gift.note || ''"
                    :from-name="gift.from_name || ''" :title="shown.title" :caption="shown.caption || ''" :reader-name="data.reader_name || ''"
                    :reader-avatar="data.reader_avatar || ''" :cards="shown.cards" :summary="shown.summary" />
      </Transition>
    </div>
  </section>
</template>

<style scoped>
.sg {
  --night: #0d0520; --lilac: #c3a9ff; --mist: #b9a6d6; --gold-lt: #e7c983; --cream: #f4ecdc;
  --ui: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  display: grid; grid-template-columns: minmax(0, 1fr);
  grid-template-areas: "top" "letter" "foot";
  row-gap: 12px; max-width: 560px; margin: 0 auto; color: #efe4ff; font-family: var(--ui);
  animation: sg-in 500ms ease-out both;
}
/* On a phone or tablet the side column melts away: its parts take their
   places around the letter (switch above, band and actions below). */
.sg-side { display: contents; }
.sg-top { grid-area: top; display: flex; align-items: center; gap: 10px; min-width: 0; }
.sg-letter { grid-area: letter; min-width: 0; }
.sg-foot { grid-area: foot; display: flex; flex-direction: column; gap: 12px; min-width: 0; }

/* The switch: two segments, the chosen one on cream. */
.sg-switch { flex: 1 1 auto; min-width: 0; display: grid; grid-template-columns: 1fr 1fr; padding: 4px; border-radius: 999px;
  background: #140a28; border: 1px solid rgba(231, 201, 131, 0.28); }
.sg-opt { min-width: 0; min-height: 44px; padding: 6px 12px; border: 0; border-radius: 999px; background: transparent;
  color: var(--mist); font: 600 13.5px/1.2 var(--ui); cursor: pointer; transition: background-color 180ms ease, color 180ms ease;
  overflow-wrap: anywhere; }
.sg-opt.on { background: var(--cream); color: #2a1845; box-shadow: 0 4px 14px -6px rgba(0, 0, 0, 0.6); }
/* "Your reading" carries the lilac of its paper. */
.sg-opt.on[data-testid="version-mine"] { background: #efe7f6; }
.sg-opt:not(.on):hover { color: #fff4dc; }
.sg-opt:focus-visible, .sg-x:focus-visible, .sg-copy:focus-visible, .sg-link:focus-visible, .sg-leave:focus-visible {
  outline: 2px solid var(--gold-lt); outline-offset: 3px; }
.sg-to { display: none; }
.sg-one { flex: 1 1 auto; min-width: 0; margin: 0 0 0 4px; font: 600 16px/1.25 var(--display); color: #fff4dc; letter-spacing: 0.02em; }
.sg-x { flex: none; width: 44px; height: 44px; border-radius: 50%; border: 1px solid rgba(231, 201, 131, 0.22);
  background: rgba(255, 255, 255, 0.08); color: #efe4ff; display: grid; place-items: center; font-size: 15px; cursor: pointer; }
.sg-x:hover { background: rgba(255, 255, 255, 0.14); }

/* The status band: slim, the state and the one next step. */
.sg-band { padding: 14px 18px 15px; border-radius: 16px; background: linear-gradient(180deg, #22123f, #170b2e);
  border: 1px solid rgba(231, 201, 131, 0.22); }
.sg-lead { margin: 0; font: italic 500 18px/1.4 var(--serif); color: #f3eaff; text-wrap: pretty; overflow-wrap: break-word; }
.sg-lead b { font-style: normal; font-weight: 600; color: var(--gold-lt); }
.sg-quote { color: var(--gold-lt); }
.sg-heart { font-style: normal; color: var(--gold-lt); }
.sg-next { margin: 6px 0 0; font: 500 13.5px/1.5 var(--ui); color: var(--mist); text-wrap: pretty; }

/* The actions. */
.sg-actions { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 4px 0 8px; }
.sg-copy { display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 48px; padding: 12px 26px;
  border-radius: 999px; border: 1px solid rgba(240, 217, 163, 0.55); background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #ffdfa3; font: 700 13px/1.2 var(--ui); letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer;
  box-shadow: 0 12px 30px -8px rgba(78, 39, 118, 0.7); max-width: 100%; }
.sg-copy:disabled { opacity: 0.6; cursor: default; }
.sg-hint { margin: 6px 0 0; font: 500 12.5px/1.45 var(--ui); color: var(--mist); text-align: center; }
.sg-link { min-height: 44px; padding: 10px 12px; border: 0; background: none; color: var(--lilac); font: 500 13.5px/1.2 var(--ui);
  text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.sg-link:hover { color: var(--gold-lt); }
.sg-strong { font-weight: 700; color: #ffdfa3; }
.sg-confirm { text-align: center; }
.sg-confirm p { margin: 6px 0 0; font: 500 14px/1.45 var(--ui); color: #e8dcff; }
.sg-confirm-row { display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; }
.sg-err { margin: 4px 0 0; color: #ffb4a0; font: 500 13.5px/1.4 var(--ui); text-align: center; }
.sg-leave { margin-top: 8px; min-height: 44px; padding: 10px 22px; border-radius: 999px; border: 1px solid rgba(233, 220, 255, 0.35);
  background: none; color: #e9dcff; font: 500 13px/1.2 var(--ui); letter-spacing: 0.04em; cursor: pointer; max-width: 100%; }
.sg-leave:hover { border-color: #ffdfa3; color: #ffdfa3; }

.sg-fade-enter-active, .sg-fade-leave-active { transition: opacity 140ms ease; }
.sg-fade-enter-from, .sg-fade-leave-to { opacity: 0; }
@keyframes sg-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) {
  .sg { animation: none; }
  .sg-fade-enter-active, .sg-fade-leave-active, .sg-opt { transition: none; }
}

/* Phones: the letter runs to a 10px gutter, as in the approved mockups (the
   page layer around it pads 16px). */
@media (max-width: 480px) {
  .sg { margin: 0 -6px; }
}
/* Narrow phones: "Chris Chi's version" stays on one line. A long name still
   wraps, balanced, rather than clip. */
@media (max-width: 400px) {
  .sg-top { gap: 8px; }
  .sg-switch { padding: 3px; }
  .sg-opt { padding: 6px 8px; font-size: 13px; letter-spacing: -0.005em; text-wrap: balance; }
}
@media (min-width: 640px) {
  .sg { max-width: 720px; }
}
/* A desktop: the switch, the band and the actions in a sticky side column,
   the letter beside it. One version at a time, never side by side. */
@media (min-width: 1024px) {
  .sg { max-width: 1120px; grid-template-columns: 300px minmax(0, 1fr); grid-template-areas: "side letter";
    column-gap: 28px; row-gap: 0; align-items: start; }
  .sg-side { display: flex; flex-direction: column; gap: 14px; grid-area: side; position: sticky; top: 0; }
  /* The column's head: who it went to and the X, then the switch the
     column's full width, so every right edge below lines up. */
  .sg-top { display: grid; grid-template-columns: minmax(0, 1fr) 44px; align-items: center; column-gap: 12px; row-gap: 14px; }
  .sg-to { display: block; grid-column: 1; grid-row: 1; margin: 0 0 0 4px; font: 600 16px/1.25 var(--display); color: #fff4dc; letter-spacing: 0.02em; text-wrap: balance; }
  .sg-x { grid-column: 2; grid-row: 1; }
  .sg-switch { grid-column: 1 / -1; grid-row: 2; grid-template-columns: 1fr; border-radius: 18px; gap: 4px; }
  .sg-top.one-version .sg-one { grid-column: 1; grid-row: 1; }
  .sg-opt { border-radius: 14px; text-align: left; padding: 8px 16px; }
  .sg-actions { align-items: stretch; }
  .sg-actions .sg-link { align-self: center; }
  .sg-copy, .sg-leave { width: 100%; }
}
@media (min-width: 1280px) {
  .sg { grid-template-columns: 320px minmax(0, 800px); justify-content: center; column-gap: 36px; }
}
</style>
